import { deepMerge, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { OnChangeFn, RowSelectionState } from '@tanstack/table-core';
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_TableOptions,
} from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSamplingQueries } from '../../data-access/queries/sampling';
import { SamplingPlansListItem } from '../../data-access/types/sampling';

import { filterOrganizationColumns, usePermissions } from '@auth/data-access';
import { FORM_STATUS, useFiltersStore, useFiltersTableOptions } from '@data-access';
import {
  formatOrganizationNames,
  OrganizationsList,
  PfTable,
  StatusCell,
  StatusColumnFilter,
  TsrButtonLink,
  useRowSelection,
} from '@ui';

interface SamplingPlansTableProps {
  rowSelection?: MRT_RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
}

export const SamplingPlansTable: FC<SamplingPlansTableProps> = ({
  rowSelection,
  setRowSelection,
}) => {
  const { t } = useTranslation();
  const { showOrganizationColumn } = usePermissions();
  const filtersTableOptions =
    useFiltersTableOptions<SamplingPlansListItem>('samplingPlans');
  const samplingQueries = useSamplingQueries();
  const page = useFiltersStore.use['samplingPlans']().pagination.pageIndex + 1;
  const sorting = useFiltersStore.use['samplingPlans']().sorting;
  const filters = useFiltersStore.use['samplingPlans']().filters;

  const samplingPlans = useQuery({
    ...samplingQueries.plans.list({ page, sorting, filters }),
    placeholderData: keepPreviousData,
  });
  const analysisTypeOptions = useMemo(
    () =>
      (['carbon', 'density', 'granulo'] as const).map((value) => ({
        value,
        label: t(`sampling.analysis_types.${value}`),
      })),
    [t],
  );

  const rowSelectionProps = useRowSelection<SamplingPlansListItem>({
    data: samplingPlans.data?.results ?? [],
    rowSelection,
    setRowSelection,
  });

  const columns = useMemo<MRT_ColumnDef<SamplingPlansListItem>[]>(
    () =>
      filterOrganizationColumns(
        [
      {
        id: 'contract__farm__name',
        accessorKey: 'farm.name',
        header: t('common.company_name'),
        Cell: ({ cell, row }) => {
          return (
            <TsrButtonLink
              fullWidth
              justify="start"
              maw={250}
              ml="calc(var(--button-padding-x-compact-md) * -1)"
              params={{ id: row.original.id.toString() }}
              size="compact-md"
              td="underline"
              to="/sampling-plans/$id"
              tt="capitalize"
              variant="subtle"
            >
              <Text span truncate="end">
                {cell.getValue<string>()}
              </Text>
            </TsrButtonLink>
          );
        },
      },
      {
        accessorKey: 'ilot_id',
        header: t('common.properties.ilot_id'),
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>();
          if (value == null) return '-';
          return value;
        },
      },
      {
        accessorKey: 'field_teledec_id',
        header: t('common.properties.field_teledec_id'),
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>();
          if (value == null) return '-';
          return value;
        },
      },
      {
        id: 'organizations',
        accessorFn: (row) => formatOrganizationNames(row.organizations),
        header: t('common.properties.organization'),
        Cell: ({ row }) => (
          <OrganizationsList organizations={row.original.organizations} />
        ),
      },
      {
        accessorKey: 'analysis_type',
        header: t('common.properties.analysis_type'),
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: analysisTypeOptions,
        },
        editVariant: 'select',
        mantineEditSelectProps: {
          variant: 'default',
          clearable: false,
          data: analysisTypeOptions,
        },
        Cell: ({ cell }) =>
          t(`sampling.analysis_types.${cell.getValue<string>()}`),
      },
      {
        accessorKey: 'nbr_samples',
        enableEditing: false,
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        header: t('common.properties.nbr_points'),
      },
      {
        accessorKey: 'nbr_points',
        enableEditing: false,
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        header: t('common.properties.nbr_samples'),
      },
      {
        accessorKey: 'area',
        enableEditing: false,
        header: t('common.area'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return '-';
          return Number(value).toFixed(2).replace('.', ',').concat(' ha');
        },
      },
      {
        accessorKey: 'status',
        header: t('common.properties.status'),
        maxSize: 100,
        Cell: ({ cell }) => {
          const status = cell.getValue<FORM_STATUS>();

          if (!status) {
            return '-';
          }

          return <StatusCell status={status} />;
        },
        Filter: ({ column, table }) => {
          return (
            <StatusColumnFilter
              onChange={(value) => column.setFilterValue(value)}
              onClear={() => column.setFilterValue(null)}
              value={column.getFilterValue() as FORM_STATUS}
            />
          );
        },
      },
        ],
        showOrganizationColumn('list'),
      ),
    [t, analysisTypeOptions, showOrganizationColumn],
  );

  const tableOptions: MRT_TableOptions<SamplingPlansListItem> = deepMerge(
    {
      data: samplingPlans.data?.results ?? [],
      rowCount: samplingPlans.data?.count ?? 0,
      columns,

      state: {
        rowSelection,
        isLoading: samplingPlans.isLoading,
        showLoadingOverlay:
          samplingPlans.isFetching && samplingPlans.isPlaceholderData,
      },

      initialState: {
        showColumnFilters: true,
      },

      mantinePaperProps: {
        h: '100%',
      },
      ...rowSelectionProps,
    },
    filtersTableOptions,
  );

  return <PfTable<SamplingPlansListItem> options={tableOptions} />;
};

export default SamplingPlansTable;
